import React, { useEffect, useState } from "react";
import Avatar from "./Avatar";
import { ChevronDownIcon, ChevronUpIcon } from "./InlineIcons";
import Reply from "./Reply";
import { ReplyData } from "./types";

interface ThreadProps {
  id: string;
  label: string;
  replyCount: number;
  replies: ReplyData[];
  remainingReplies?: number;
  currentUserName: string;
  currentUserProfilePictureUrl?: string;
  initiallyReplyingToId?: string;
  onCollapse: () => void;
  onLoadMore?: () => void;
  onReplySubmit?: (commentId: number | null, value: string) => Promise<void>;
  onDeleteReply?: (commentId: number, replyId?: number) => void;
}

const Thread = ({
  id,
  label,
  replyCount,
  replies,
  remainingReplies,
  currentUserName,
  currentUserProfilePictureUrl,
  initiallyReplyingToId,
  onCollapse,
  onLoadMore,
  onReplySubmit,
  onDeleteReply,
}: ThreadProps) => {
  const [replyingTo, setReplyingTo] = useState<string | null>(
    initiallyReplyingToId || null,
  );
  const [threadReply, setThreadReply] = useState("");
  const [threadReplies, setThreadReplies] = useState(replies);
  const [submittingThreadReply, setSubmittingThreadReply] = useState(false);

  useEffect(() => {
    setThreadReplies(replies);
  }, [replies]);

  const toggleReply = (reply: ReplyData) => {
    setReplyingTo(replyingTo === reply.id ? null : reply.id);
  };

  const submitInlineReply = async (commentId: number, value: string) => {
    await onReplySubmit?.(commentId, value);
    setReplyingTo(null);
  };

  const submitThreadReply = async () => {
    const value = threadReply.trim();
    if (!value || submittingThreadReply) return;

    setSubmittingThreadReply(true);
    await onReplySubmit?.(null, value);
    setThreadReply("");
    setSubmittingThreadReply(false);
  };

  return (
    <section className="thread-expansion is-open" id={id} aria-label={label}>
      <div className="thread-toolbar">
        <div className="thread-toolbar-left">
          <strong>{replyCount ?? 0} replies</strong>
        </div>
      </div>

      <ol className="reply-list">
        {threadReplies.map((reply) => (
          <Reply
            reply={reply}
            replyingToId={replyingTo}
            currentUserName={currentUserName}
            currentUserProfilePictureUrl={currentUserProfilePictureUrl}
            composerId={
              reply.id === initiallyReplyingToId
                ? `reply-input-${reply.id}`
                : undefined
            }
            onReply={toggleReply}
            onCancelReply={() => setReplyingTo(null)}
            onReplySubmit={submitInlineReply}
            onDeleteReply={onDeleteReply}
            key={reply.id}
          />
        ))}
      </ol>

      {remainingReplies !== undefined && (
        <button className="thread-load-more" type="button" onClick={onLoadMore}>
          Show {remainingReplies} more replies <ChevronDownIcon />
        </button>
      )}

      <div className="thread-composer">
        <Avatar
          className="thread-composer-avatar"
          name={currentUserName}
          profilePictureUrl={currentUserProfilePictureUrl}
        />
        <input
          className="thread-composer-input"
          type="text"
          placeholder="Add to the thread..."
          aria-label="Write a reply"
          value={threadReply}
          onChange={(event) => setThreadReply(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;

            event.preventDefault();
            submitThreadReply();
          }}
        />
        <button
          className="thread-composer-btn"
          type="button"
          disabled={!threadReply.trim() || submittingThreadReply}
          onClick={submitThreadReply}
        >
          {submittingThreadReply && <span className="reply-submit-spinner" />}
          {submittingThreadReply ? "Posting..." : "Reply"}
        </button>
      </div>

      <div className="thread-collapse-wrap">
        <button className="thread-collapse" type="button" onClick={onCollapse}>
          Collapse thread <ChevronUpIcon />
        </button>
      </div>
    </section>
  );
};

export default Thread;
